import React, { useContext, useEffect, useRef, useState } from "react";
import InfoTip from "../shared/InfoTip";
import { ErrorState } from "../shared/StateMessage";
import PanelErrorBoundary, { sanitizePanelError } from "../shared/PanelErrorBoundary";
import { SkeletonText, RefetchBar as _RefetchBar } from "../shared/Skeleton";

// Re-exported for back-compat with existing CI panel imports
// (`import { RefetchBar } from "./AnalysisPanel"`). New code should import
// directly from `../shared/Skeleton`. 2026-07-29 — renamed the local
// binding + re-export explicitly (was `export { RefetchBar };` right after
// the import, which webpack occasionally dropped from a stale build's
// module.exports listing).
const RefetchBar = _RefetchBar;
export { RefetchBar };

// When the page wraps panels in a tab UI, the tab label/active state already plays
// the role of the panel's own header + chevron. Provide this context to suppress
// both and force the body to render — without threading props through every panel.
export const AnalysisPanelContext = React.createContext({ forceOpen: false, hideHeader: false });

// Reusable collapsible wrapper for Section 8 analysis panels.
// Lazy-fetches data on first expand so the page doesn't fire all 24 requests on mount.
//
// Props:
//   id          — stable id used for fetch deduping when deps change
//   title       — panel title
//   description — short subtitle below the title
//   badge       — optional small label (e.g. category code) shown in header
//   fetcher     — async () => response. Called on first expand, and re-called when `deps` change.
//                 Ignored when `external` is provided.
//   deps        — dependency array; if any value changes while the panel is open, refetch
//   defaultOpen — whether to start expanded (default false)
//   forceOpen   — when true, panel is always open and the collapse toggle/chevron is hidden
//   hideHeader  — when true, omit the title/description header (used when a parent tab provides the label)
//   external    — { data, loading, error }. When provided, AnalysisPanel skips its internal
//                 fetch lifecycle and uses the supplied state. Lets a parent own caching/pagination
//                 (e.g. via usePaginatedCache) while reusing the panel's chrome and refetch UX.
//   onOpen      — called when the panel transitions from closed to open (used by external mode
//                 to lazy-trigger the parent's fetch on first expand)
//   children    — render-prop: ({ data, loading, error }) => ReactNode
const AnalysisPanel = ({
  id,
  title,
  description,
  badge,
  tip,
  fetcher,
  deps = [],
  defaultOpen = false,
  forceOpen = false,
  hideHeader = false,
  external = null,
  onOpen,
  children,
}) => {
  const ctx = useContext(AnalysisPanelContext);
  const effectiveForceOpen = forceOpen || ctx.forceOpen;
  const effectiveHideHeader = hideHeader || ctx.hideHeader;
  const [openState, setOpen] = useState(defaultOpen);
  const open = effectiveForceOpen || openState;
  const [state, setState] = useState({ loading: false, error: "", data: null, fetched: false });
  const reqIdRef = useRef(0);

  // Notify parent on first/each open transition (external mode uses this to gate fetching).
  useEffect(() => {
    if (open && onOpen) onOpen();
  }, [open, onOpen]);

  // `deps` is spread into the effect's dependency list below, which React requires to
  // be CONSTANT-SIZE between renders. Every current caller passes a fixed-length
  // literal, so this was latent rather than broken — but a caller building deps
  // conditionally would trip React's "size changed" error. Serializing to one string
  // makes the list fixed-width regardless of what callers pass. Safe because every
  // dep in use is a primitive (client id, category code, numeric threshold).
  const depsKey = JSON.stringify(deps);

  // `fetcher` is deliberately NOT a dependency: callers pass an inline arrow, so it is
  // a fresh reference on every render and depending on it would refetch in a loop. The
  // deps array is the intended refetch trigger; the ref below keeps the effect calling
  // the latest closure without re-subscribing to it.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;

  // Refetch when the panel is open and deps change (or on first expand).
  // Skipped entirely when external state is supplied — the parent owns the lifecycle.
  useEffect(() => {
    if (external) return;
    if (!open) return;
    const myReq = ++reqIdRef.current;
    setState((prev) => ({ ...prev, loading: true, error: "" }));
    Promise.resolve()
      .then(() => fetcherRef.current())
      .then((resp) => {
        if (myReq !== reqIdRef.current) return;
        const payload = resp?.data ?? resp ?? null;
        setState({ loading: false, error: "", data: payload, fetched: true });
      })
      .catch((err) => {
        if (myReq !== reqIdRef.current) return;
        setState({ loading: false, error: err?.message || "Failed to load", data: null, fetched: true });
      });
  }, [external, open, id, depsKey]);

  // When external state is supplied, mirror it into local state so the render branch below
  // doesn't need to know which mode it's in.
  const effectiveState = external
    ? { loading: !!external.loading, error: external.error || "", data: external.data ?? null }
    : state;

  const showHeader = !effectiveHideHeader;
  const showToggle = !effectiveForceOpen;

  return (
    <div className="bg-white dark:bg-gray-900 rounded-xl shadow border border-gray-200 dark:border-gray-800 overflow-hidden">
      {showHeader && showToggle && (
        <button
          type="button"
          onClick={() => setOpen((prev) => !prev)}
          aria-expanded={open}
          className="w-full px-5 py-3 flex items-start justify-between gap-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors focus:outline-none focus:ring-2 focus:ring-[#32B1FF] focus:ring-inset"
        >
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
                {title}
                {tip && <InfoTip text={tip} />}
              </h3>
              {badge && (
                <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {badge}
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
            )}
          </div>
          <svg
            className={`w-5 h-5 text-gray-400 dark:text-gray-500 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      )}

      {showHeader && !showToggle && (
        <div className="px-5 py-3 flex items-start gap-3">
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100 inline-flex items-center">
                {title}
                {tip && <InfoTip text={tip} />}
              </h3>
              {badge && (
                <span className="text-[11px] uppercase tracking-wide font-semibold px-2 py-0.5 rounded bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {badge}
                </span>
              )}
            </div>
            {description && (
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
            )}
          </div>
        </div>
      )}

      {open && (
        <div className={`relative ${showHeader ? "border-t border-gray-200 dark:border-gray-800" : ""}`}>
          {/* First load: full skeleton. Subsequent refetches: keep prior content visible
              and indicate refresh via a top progress bar + dimmed body. */}
          {effectiveState.loading && effectiveState.data == null && !effectiveState.error && <PanelSkeleton />}
          {effectiveState.error && <ErrorState variant="inset" message={sanitizePanelError(effectiveState.error)} />}
          {!effectiveState.error && effectiveState.data != null && (
            <>
              {effectiveState.loading && <RefetchBar />}
              <div className={effectiveState.loading ? "opacity-60 transition-opacity" : "transition-opacity"}>
                {/* Render-phase crashes in a panel body (undefined.map on an
                    unexpected payload shape, etc.) previously propagated up and
                    blanked the whole page. Scope them to this panel so the rest of
                    the dashboard survives. Wrapping only the children() call keeps
                    the header/skeleton/refetch chrome intact, and routes the crash
                    through the same sanitize + ErrorState pipe as a failed fetch. */}
                <PanelErrorBoundary label={id || title}>
                  {children({ data: effectiveState.data, loading: effectiveState.loading, error: effectiveState.error })}
                </PanelErrorBoundary>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

const PanelSkeleton = () => (
  <div className="p-5">
    <SkeletonText lines={5} />
  </div>
);

export const PanelEmpty = ({ message }) => (
  <div className="p-8 text-center text-sm text-gray-500 dark:text-gray-400">{message}</div>
);

export default AnalysisPanel;
